"use client";

import {formatDistanceToNow} from "date-fns";
import {Clock, Monitor, Smartphone, Tablet} from "lucide-react";
import {useTranslation} from "react-i18next";
import {LoaderIcon} from "@/components/icons";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import {useSessions} from "@/hooks/use-api-endpoint";

type SessionsListProps = {
    userId: string;
};

export function SessionsList({userId}: SessionsListProps) {
    const {t} = useTranslation();
    const {sessions, isLoading, error} = useSessions();

    const getDeviceIcon = (browserName: string, deviceModel: string) => {
        browserName = browserName.toUpperCase();
        deviceModel = deviceModel.toUpperCase();

        if (deviceModel === "TABLET" || browserName.includes("TABLET")) {
            return <Tablet className="h-4 w-4"/>;
        }

        if (deviceModel === "MOBILE" || browserName.includes("MOBILE")) {
            return <Smartphone className="h-4 w-4"/>;
        }

        return <Monitor className="h-4 w-4"/>;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <LoaderIcon className="h-6 w-6 animate-spin text-muted-foreground"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <p className="text-destructive text-sm">{t(LocalizationKeys.ERROR_SESSIONS_LOAD_FAILED)}</p>
            </div>
        );
    }

    if (sessions.length === 0) {
        return (
            <div className="rounded-lg border bg-muted/30 p-6 text-center">
                <p className="text-muted-foreground text-sm">
                    {t(LocalizationKeys.SESSIONS_EMPTY)}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {sessions.map((session) => (
                <div
                    key={session.sessionId}
                    className="rounded-lg border bg-muted/30 p-4 space-y-2"
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="mt-0.5 text-muted-foreground flex-shrink-0">
                                {getDeviceIcon(session.browserName, session.deviceModel)}
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                                <p className="font-medium text-sm">
                                    {session.browserName}
                                </p>
                                <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                                    <Clock className="h-3 w-3"/>
                                    <span>
                                        {t(LocalizationKeys.SESSIONS_CREATED_AGO, {
                                            time: formatDistanceToNow(new Date(session.createdAt))
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <p className="text-muted-foreground text-xs pt-2">
                {t(LocalizationKeys.SESSIONS_COUNT, {count: sessions.length})}
            </p>
        </div>
    );
}
